import type { LocaleCode } from '@/i18n/locales'
import { OnboardingFlow, entryStep } from '@/onboarding/engine'
import type { OnboardingEffect } from '@/onboarding/engine'
import type { OnboardingStepId } from '@/onboarding/steps'
import { readVolunteered } from '@/onboarding/volunteered'
import type { ProfileWriter } from '@/onboarding/writer'
import type { Profile } from '@/profile/types'
import type { ListenResult } from '@/voice/types'

/**
 * Turning the onboarding engine's effects into a conversation.
 *
 * `src/onboarding/engine.ts` is a synchronous reducer: events go in, a list of
 * things to do comes out, and nothing in it awaits anything. Something has to
 * speak those lines, open the microphone, and feed the answer back, and that is
 * this file. It is the only place in the first-run experience that knows both
 * halves.
 *
 * Written as a plain factory rather than a hook so the whole first
 * conversation, including the paths nobody reaches by hand, can be driven from
 * a test with two fake functions and no browser. The React binding is
 * `useOnboarding.ts` and it is deliberately thin.
 *
 * Three things in here are not obvious and each one is a specific failure.
 *
 * A skip has to cut the sentence short. The engine already understands "skip"
 * as an answer, so the naive wiring is to feed the word in as a transcript and
 * let `isDeferral` catch it. That leaves the open microphone turn resolving a
 * second later with whatever the room happened to say, against a step the flow
 * has already left. So the pressed control cancels the speech, aborts the turn,
 * and the loop reads the request at the top of its next pass.
 *
 * The microphone is asked for at the first turn that needs it, not before the
 * first word. A permission prompt fires once and is remembered forever, so the
 * one made before somebody knows what the app is for is the one most likely to
 * be refused. By the time this asks, the opening has been spoken in both
 * languages and the person has answered it.
 *
 * Server writes are never awaited. The local copy has already landed, the
 * person is mid-sentence, and there is nothing useful to say to them about an
 * HTTP status. `writer.ts` folds a failure into the next write.
 */

/**
 * The four answers that become petals.
 *
 * The open conversation is a step but it is not an answer, so it does not
 * bloom: the orb fills with what Namu has learned, and "I asked you to talk to
 * me" is not something it learned. Age band and gender are absent for the same
 * reason they are absent from the flow, and a volunteered one arriving in the
 * middle of a sentence must not make a petal appear out of nowhere.
 */
const PETAL_SLOTS = ['language', 'consent', 'name', 'region'] as const

export const PETAL_TOTAL = PETAL_SLOTS.length

export function petalsFor(profile: Profile): number {
  return PETAL_SLOTS.filter((slot) => profile[slot] !== null).length
}

/**
 * What the surface shows before a word has been spoken.
 *
 * Exported so a caller can seed its own state with it and only then build the
 * runner. It is derived from the stored profile alone, which is also what
 * makes it correct: somebody returning to a half-finished onboarding sees the
 * petals they have already earned on the very first frame, rather than an
 * empty ring that fills in after the first question is answered again.
 */
export function initialView(profile: Profile): OnboardingView {
  const stepId = entryStep(profile)
  return {
    active: stepId !== 'open',
    asking: false,
    stepId,
    petals: petalsFor(profile),
    disclosure: null,
    typedOffered: false,
  }
}

/** Everything the first-run surface renders, and nothing else. */
export interface OnboardingView {
  /**
   * Whether onboarding still has something to ask. Drives the bloom, and stays
   * true through the open conversation, because the place question has not
   * been asked yet and the orb is honestly not full.
   */
  active: boolean
  /**
   * Whether a question is in flight right now. This is what puts the skip on
   * screen and takes the control rail off it, so the promise that there is one
   * control at a time holds without anybody counting buttons.
   */
  asking: boolean
  stepId: OnboardingStepId
  petals: number
  /** The four consent points, on screen for exactly as long as that step runs. */
  disclosure: readonly string[] | null
  /** Set once the keyboard has been offered for the step being asked. */
  typedOffered: boolean
}

/**
 * What the runner needs from the outside world.
 *
 * Named functions rather than the `voiceSession` singleton, because a test that
 * has to stand up a transport to assert that declining consent skips the name
 * question is a test nobody writes.
 */
export interface OnboardingHost {
  speak(text: string, options: { interruptible: boolean }): Promise<void>
  /** One turn of the microphone. Resolves `aborted` when the signal fires. */
  listen(signal: AbortSignal): Promise<ListenResult>
  /** Stops the line being spoken, so a skip does not wait out the sentence. */
  cancelSpeech(): void
  /** Feeds a typed answer in as though it had been spoken. */
  submitTyped(text: string): void
  /**
   * The reply settled the language. Called once, with the profile carrying the
   * language slot, so the caller can move the copy, the document and the voice
   * together.
   */
  applyLocale(profile: Profile): void
  writer: ProfileWriter
}

export interface OnboardingRunnerOptions {
  host: OnboardingHost
  /** Whatever the app is currently in. The answer for silence. */
  deviceLocale: LocaleCode
  /** What is already on the device, so a second run resumes rather than repeats. */
  profile: Profile
  onChange(view: OnboardingView): void
}

export interface RunOptions {
  signal: AbortSignal
  /**
   * Asks for the microphone and reports whether listening is now possible.
   *
   * Supplied per session rather than fixed on the host, because the caller
   * speaks a primer first and that primer belongs to the session that is about
   * to ask. Called at the first turn that needs it, never before: a permission
   * prompt fires once and is remembered forever, so the one made before
   * somebody knows what the app is for is the one most likely to be refused.
   */
  ensureMicrophone(): Promise<boolean>
}

export interface OnboardingRunner {
  /** Whether there is anything left to ask. Decided once, at construction. */
  readonly due: boolean
  /**
   * True when onboarding's own bilingual line is this session's opening, so
   * the returning greeting must not also be spoken. See `useConversation`.
   */
  readonly opensTheConversation: boolean
  readonly view: OnboardingView
  /** Asks everything up to the invitation. Resolves when Namu takes the floor. */
  run(options: RunOptions): Promise<void>
  /** The open conversation answered something well. Place is now earned. */
  served(signal: AbortSignal): Promise<void>
  /** Accepts an age or a gender offered in passing. Never asks for either. */
  overhear(transcript: string, locale: LocaleCode): void
  skip(): void
  answerTyped(text: string): void
}

export function createOnboardingRunner(options: OnboardingRunnerOptions): OnboardingRunner {
  const { host, onChange } = options

  /*
   * Resumed from the stored profile rather than from a saved snapshot, and
   * that is the engine's own reasoning rather than a shortcut. Every step
   * writes as it completes, so `entryStep` recovers the question the person
   * was on. What a snapshot would additionally restore is a read-back in
   * flight, and asking somebody to confirm a name they cannot remember hearing
   * is worse than asking for the name again.
   */
  const flow = new OnboardingFlow({
    deviceLocale: options.deviceLocale,
    profile: options.profile,
  })
  const due = flow.stepId !== 'open'

  let view: OnboardingView = initialView(options.profile)

  let skipRequested = false
  let turn: AbortController | null = null
  let microphoneReady = false
  let openMicrophone: (() => Promise<boolean>) | null = null

  function publish(patch: Partial<OnboardingView> = {}): void {
    const next: OnboardingView = {
      ...view,
      stepId: flow.stepId,
      petals: petalsFor(flow.profile),
      typedOffered: flow.typedOffered,
      active: due && flow.settled === null,
      ...patch,
    }
    /*
     * The disclosure belongs to the consent step and to nothing else, so it is
     * cleared by rule rather than by a matching effect. There are four ways out
     * of that step, counting a decline, a skip, running out of attempts and a
     * yes, and the one that gets forgotten is the one that leaves the terms
     * sitting on screen above a question about somebody's name.
     */
    view = next.stepId === 'consent' ? next : { ...next, disclosure: null }
    onChange(view)
  }

  /**
   * Works through a batch of effects, asking for more when a turn produces
   * them, until the flow has nothing further to do.
   *
   * A `listen` effect is always the last in the batch the engine emits, so
   * appending what the turn produces preserves the order the engine intended.
   */
  async function drain(initial: readonly OnboardingEffect[], signal: AbortSignal): Promise<void> {
    const queue: OnboardingEffect[] = [...initial]
    publish({ asking: true })

    try {
      while (!signal.aborted) {
        /*
         * Tested before the queue is, not after. A skip pressed during the
         * microphone turn aborts it, the turn produces no effects, and the
         * queue is empty by the time control returns here: checking the queue
         * first ended the conversation on the step the person had just asked
         * to leave, silently, and the flow never heard about it.
         */
        if (skipRequested) {
          skipRequested = false
          // Everything still queued belonged to the question being skipped.
          // Draining it anyway speaks the rest of a sentence somebody has just
          // said they do not want.
          queue.length = 0
          queue.push(...flow.receive({ type: 'skip' }))
          continue
        }

        const effect = queue.shift()
        if (effect === undefined) break
        queue.push(...(await apply(effect, signal)))
      }
    } finally {
      publish({ asking: false })
    }
  }

  async function apply(
    effect: OnboardingEffect,
    signal: AbortSignal,
  ): Promise<readonly OnboardingEffect[]> {
    switch (effect.kind) {
      case 'say':
        await host.speak(effect.text, { interruptible: effect.interruptible })
        return []

      case 'disclose':
        publish({ disclosure: effect.points })
        return []

      case 'offerTyped':
        publish()
        return []

      case 'persist':
        // The language is applied before anything else is said, because the
        // very next line comes out of the script for whichever locale this is.
        if (effect.slot === 'language') host.applyLocale(effect.profile)
        void host.writer.apply(effect)
        publish()
        return []

      case 'settle':
        void host.writer.apply(effect)
        publish()
        return []

      case 'handover':
        // Namu is asking nothing further for now. The rail comes back and the
        // skip goes away, because there is no longer a question to skip.
        publish({ asking: false })
        return []

      case 'listen':
        return listenOnce(signal)
    }
  }

  async function listenOnce(signal: AbortSignal): Promise<readonly OnboardingEffect[]> {
    if (!microphoneReady && openMicrophone) {
      microphoneReady = await openMicrophone()
      if (!microphoneReady) {
        /*
         * A blocked microphone abandons rather than declines.
         *
         * `abandoned` is the one settle reason that leaves `completedAt` null,
         * so the questions are asked again another day. Anything else records a
         * decision the person did not make: they did not refuse to be
         * remembered, their browser refused to listen, and the two must not end
         * up in the same column of the same table.
         */
        return flow.receive({ type: 'abandon' })
      }
      if (signal.aborted) return []
    }

    const active = new AbortController()
    turn = active
    const stop = () => active.abort()
    signal.addEventListener('abort', stop, { once: true })

    try {
      const result = await host.listen(active.signal)
      // Checked before the status. A skip aborts the turn, and reading the
      // resulting `aborted` as a failed attempt would spend one of the three
      // the person has, on a question they have just declined to answer.
      if (skipRequested || signal.aborted) return []

      switch (result.status) {
        case 'final':
          // Read before the answer is parsed, so "I am 62, call me Amina"
          // records both. See `volunteered.ts` for why this is not simply
          // running the extractors over everything.
          overhear(result.transcript, flow.locale)
          return flow.receive({
            type: 'heard',
            transcript: result.transcript,
            confidence: result.confidence,
          })
        case 'typed':
          return flow.receive({ type: 'heard', transcript: result.transcript, typed: true })
        case 'timeout':
          return flow.receive({ type: 'silence' })
        case 'unintelligible':
          return flow.receive({ type: 'unintelligible' })
        case 'aborted':
          return []
      }
    } finally {
      signal.removeEventListener('abort', stop)
      turn = null
    }
  }

  function overhear(transcript: string, locale: LocaleCode): void {
    const effects = flow.accept(readVolunteered(transcript, locale))
    if (effects.length === 0) return
    for (const effect of effects) void host.writer.apply(effect)
    publish()
  }

  return {
    due,
    /*
     * Only when the very first step is still ahead. Somebody who gave a
     * language last week and closed the app is a returning person: they get the
     * greeting that knows what day it is, and then the question they stopped
     * on. Greeting them bilingually again would be asking a question that has
     * already been answered.
     */
    opensTheConversation: flow.stepId === 'language',

    get view() {
      return view
    },

    async run({ signal, ensureMicrophone }) {
      if (!due) return
      openMicrophone = ensureMicrophone
      try {
        await drain(flow.receive({ type: 'begin' }), signal)
      } finally {
        /*
         * Ending the conversation, or navigating away, in the middle of a
         * question. What was captured is captured, and the rest is asked
         * another day rather than being settled against the person's interest.
         */
        if (signal.aborted && flow.settled === null) {
          for (const effect of flow.receive({ type: 'abandon' })) void host.writer.apply(effect)
          publish()
        }
      }
    },

    async served(signal) {
      if (flow.settled !== null) return
      await drain(flow.receive({ type: 'served' }), signal)
    },

    overhear,

    skip() {
      skipRequested = true
      /*
       * Both, and in this order. Cancelling the speech resolves whatever line
       * is being spoken so the loop can reach the top of its next pass;
       * aborting the turn does the same for an open microphone. Only one of
       * them is ever in flight, and doing only the one that happens to be
       * running means the control works during questions and does nothing
       * during answers, which reads as a broken button rather than a rule.
       */
      host.cancelSpeech()
      turn?.abort()
    },

    answerTyped(text) {
      const answer = text.trim()
      if (answer === '') return
      host.submitTyped(answer)
    },
  }
}
